import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronRight, Search, X, Check, FolderTree, MapPin } from 'lucide-react';

/**
 * TreeBudgetCategorySelect - Selector combinado con árbol colapsable para categorías y centros de costo
 */
export const TreeBudgetCategorySelect = ({ 
  categoriaId,
  centroCostoId,
  onValueChange,
  categorias = [],
  centrosCosto = [],
  placeholder = "Seleccionar..."
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedPadres, setExpandedPadres] = useState({});
  const [activeTab, setActiveTab] = useState('categorias'); // 'categorias' | 'centros'
  const inputRef = useRef(null);

  // Organizar categorías jerárquicamente
  const estructuraCategorias = useMemo(() => {
    const padres = categorias.filter(c => !c.padre_id);
    const hijos = categorias.filter(c => c.padre_id);
    
    if (padres.length === 0 || padres.length === categorias.length) {
      return { tipo: 'plana', items: categorias };
    }
    
    const hijosPorPadre = {};
    hijos.forEach(h => {
      if (!hijosPorPadre[h.padre_id]) hijosPorPadre[h.padre_id] = [];
      hijosPorPadre[h.padre_id].push(h);
    });
    
    return { 
      tipo: 'jerarquica', 
      padres: padres.map(p => ({
        ...p,
        hijos: hijosPorPadre[p.id] || []
      }))
    };
  }, [categorias]);

  // Filtrar por búsqueda
  const filteredCategorias = useMemo(() => {
    if (!search.trim()) return estructuraCategorias;
    
    const searchLower = search.toLowerCase();
    
    if (estructuraCategorias.tipo === 'plana') {
      return {
        tipo: 'plana',
        items: estructuraCategorias.items.filter(c => c.nombre.toLowerCase().includes(searchLower))
      };
    }
    
    const filteredPadres = estructuraCategorias.padres
      .map(padre => {
        const padreMatch = padre.nombre.toLowerCase().includes(searchLower);
        const hijosMatch = padre.hijos.filter(h => h.nombre.toLowerCase().includes(searchLower));
        
        if (padreMatch || hijosMatch.length > 0) {
          return { ...padre, hijos: padreMatch ? padre.hijos : hijosMatch, _forceExpand: true };
        }
        return null;
      })
      .filter(Boolean);
    
    return { tipo: 'jerarquica', padres: filteredPadres };
  }, [estructuraCategorias, search]);

  const filteredCentros = useMemo(() => {
    if (!search.trim()) return centrosCosto;
    return centrosCosto.filter(c => c.nombre.toLowerCase().includes(search.toLowerCase()));
  }, [centrosCosto, search]);

  // Encontrar selección actual
  const seleccionActual = useMemo(() => {
    if (categoriaId) {
      const cat = categorias.find(c => c.id === categoriaId);
      if (cat) {
        const padre = cat.padre_id ? categorias.find(p => p.id === cat.padre_id) : null;
        return { tipo: 'categoria', nombre: cat.nombre, padreNombre: padre?.nombre };
      }
    }
    if (centroCostoId) {
      const cc = centrosCosto.find(c => c.id === centroCostoId);
      if (cc) return { tipo: 'centro', nombre: cc.nombre };
    }
    return null;
  }, [categoriaId, centroCostoId, categorias, centrosCosto]);

  const togglePadre = (padreId, e) => {
    e.stopPropagation();
    setExpandedPadres(prev => ({ ...prev, [padreId]: !prev[padreId] }));
  };

  const handleSelectCategoria = (cat) => {
    onValueChange({ categoria_id: cat.id, centro_costo_id: null });
    setOpen(false);
    setSearch('');
  };

  const handleSelectCentro = (cc) => {
    onValueChange({ categoria_id: null, centro_costo_id: cc.id });
    setOpen(false);
    setSearch('');
  };

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const isPadreExpanded = (padre) => {
    if (search.trim() && padre._forceExpand) return true;
    return expandedPadres[padre.id] || false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-56 justify-between font-normal h-9", !seleccionActual && "text-muted-foreground")}
        >
          <span className="truncate">
            {seleccionActual ? (
              <span className="flex items-center gap-1">
                {seleccionActual.tipo === 'centro' && <MapPin className="h-3 w-3 text-blue-500" />}
                {seleccionActual.padreNombre && (
                  <span className="text-gray-400 text-xs">{seleccionActual.padreNombre} /</span>
                )}
                <span>{seleccionActual.nombre}</span>
              </span>
            ) : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('categorias')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium transition-colors",
              activeTab === 'categorias' 
                ? "text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <FolderTree className="h-4 w-4 inline-block mr-1" />
            Categorías
          </button>
          <button
            onClick={() => setActiveTab('centros')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium transition-colors",
              activeTab === 'centros' 
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <MapPin className="h-4 w-4 inline-block mr-1" />
            Centros Costo
          </button>
        </div>

        {/* Buscador */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              placeholder={activeTab === 'categorias' ? "Buscar categoría..." : "Buscar centro..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Contenido */}
        <div className="max-h-64 overflow-y-auto p-1">
          {activeTab === 'categorias' ? (
            // Categorías
            filteredCategorias.tipo === 'plana' ? (
              filteredCategorias.items.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-sm">No encontrado</div>
              ) : (
                filteredCategorias.items.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelectCategoria(cat)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100 flex items-center justify-between",
                      categoriaId === cat.id && "bg-emerald-50 text-emerald-700"
                    )}
                  >
                    {cat.nombre}
                    {categoriaId === cat.id && <Check className="h-4 w-4" />}
                  </button>
                ))
              )
            ) : (
              filteredCategorias.padres.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-sm">No encontrado</div>
              ) : (
                filteredCategorias.padres.map(padre => (
                  <div key={padre.id} className="mb-1">
                    <button
                      onClick={(e) => togglePadre(padre.id, e)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm font-medium text-gray-700"
                    >
                      {isPadreExpanded(padre) ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      <FolderTree className="h-4 w-4 text-emerald-500" />
                      <span className="flex-1 text-left">{padre.nombre}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{padre.hijos.length}</span>
                    </button>
                    
                    {isPadreExpanded(padre) && padre.hijos.length > 0 && (
                      <div className="ml-4 mt-1 border-l-2 border-gray-100 pl-2">
                        {padre.hijos.map(hijo => (
                          <button
                            key={hijo.id}
                            onClick={() => handleSelectCategoria(hijo)}
                            className={cn(
                              "w-full text-left px-3 py-1.5 rounded-md text-sm hover:bg-gray-100 flex items-center justify-between",
                              categoriaId === hijo.id && "bg-emerald-50 text-emerald-700"
                            )}
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                              {hijo.nombre}
                            </span>
                            {categoriaId === hijo.id && <Check className="h-4 w-4" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )
            )
          ) : (
            // Centros de Costo
            filteredCentros.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm">No encontrado</div>
            ) : (
              filteredCentros.map(cc => (
                <button
                  key={cc.id}
                  onClick={() => handleSelectCentro(cc)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100 flex items-center justify-between",
                    centroCostoId === cc.id && "bg-blue-50 text-blue-700"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    {cc.nombre}
                  </span>
                  {centroCostoId === cc.id && <Check className="h-4 w-4" />}
                </button>
              ))
            )
          )}
        </div>

        {/* Limpiar */}
        {(categoriaId || centroCostoId) && (
          <div className="border-t p-2">
            <button
              onClick={() => { onValueChange({ categoria_id: null, centro_costo_id: null }); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 rounded-md text-sm text-gray-500 hover:bg-gray-100"
            >
              Limpiar selección
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TreeBudgetCategorySelect;
